import { useState, useRef, useEffect } from 'react';

export interface QueueItem {
  id: string;
  prompt: string;
  selectedType: string;
  duration: string;
  aspectRatio: string;
  style: string;
  resolution: string;
  audioOption: string;
  status: 'pending' | 'generating' | 'done' | 'failed';
  addedAt: number;
  label: string;
  thumbnail?: string;
  progress: number;
}

interface GenerationQueueProps {
  isOpen: boolean;
  onClose: () => void;
  items: QueueItem[];
  onItemsChange: (items: QueueItem[]) => void;
  darkMode: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  marketing: 'Marketing',
  film: 'Film/Cinema',
  documentary: 'Documentary',
  social: 'Social Media',
  explainer: 'Explainer',
  animation: 'Animation',
};

const THUMBNAILS = [
  'https://public.readdy.ai/ai/img_res/ae3fb1267c3a02025b8e8ec50427bd9a.jpg',
  'https://readdy.ai/api/search-image?query=aerial%20drone%20city%20skyline%20golden%20hour%20sunset%20beautiful%20landscape%20cinematic%20shot&width=640&height=360&seq=queue-thumb-02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=product%20photography%20close%20up%20luxury%20item%20studio%20lighting%20minimal%20background%20elegant&width=640&height=360&seq=queue-thumb-03&orientation=landscape',
  'https://readdy.ai/api/search-image?query=nature%20documentary%20wildlife%20forest%20animals%20cinematic%20color%20grading%20wide%20shot&width=640&height=360&seq=queue-thumb-04&orientation=landscape',
  'https://readdy.ai/api/search-image?query=fashion%20editorial%20model%20urban%20street%20photography%20cinematic%20moody%20lighting&width=640&height=360&seq=queue-thumb-05&orientation=landscape',
  'https://readdy.ai/api/search-image?query=travel%20destination%20beach%20tropical%20paradise%20clear%20water%20stunning%20scenery%20cinematic&width=640&height=360&seq=queue-thumb-06&orientation=landscape',
];

function getStatusBadge(status: QueueItem['status'], darkMode: boolean) {
  switch (status) {
    case 'pending': return `px-2 py-0.5 rounded-full text-[10px] font-bold ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`;
    case 'generating': return `px-2 py-0.5 rounded-full text-[10px] font-bold ${darkMode ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700'}`;
    case 'done': return `px-2 py-0.5 rounded-full text-[10px] font-bold ${darkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700'}`;
    case 'failed': return `px-2 py-0.5 rounded-full text-[10px] font-bold ${darkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-600'}`;
  }
}

function getStatusIcon(status: QueueItem['status']) {
  switch (status) {
    case 'pending': return 'ri-time-line';
    case 'generating': return 'ri-loader-4-line animate-spin';
    case 'done': return 'ri-check-line';
    case 'failed': return 'ri-close-line';
  }
}

function formatTimeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

const STEP_LABELS = ['Analyzing', 'Storyboard', 'Rendering', 'Styling', 'Audio', 'Finalizing'];

export default function GenerationQueue({ isOpen, onClose, items, onItemsChange, darkMode }: GenerationQueueProps) {
  const [isRunning, setIsRunning] = useState(false);
  const runningRef = useRef(false);

  useEffect(() => {
    runningRef.current = isRunning;
  }, [isRunning]);

  const pendingCount = items.filter(i => i.status === 'pending').length;
  const doneCount = items.filter(i => i.status === 'done').length;
  const failedCount = items.filter(i => i.status === 'failed').length;

  const removeItem = (id: string) => {
    if (isRunning) return;
    onItemsChange(items.filter(i => i.id !== id));
  };

  const clearDone = () => {
    onItemsChange(items.filter(i => i.status !== 'done'));
  };

  const clearAll = () => {
    if (isRunning) return;
    onItemsChange([]);
  };

  const retryFailed = () => {
    onItemsChange(items.map(i => i.status === 'failed' ? { ...i, status: 'pending', progress: 0 } : i));
  };

  const runQueue = async () => {
    if (isRunning || pendingCount === 0) return;
    setIsRunning(true);
    runningRef.current = true;

    const pendingItems = items.filter(i => i.status === 'pending');

    for (const item of pendingItems) {
      if (!runningRef.current) break;

      // Mark as generating
      onItemsChange(prev => prev.map(i =>
        i.id === item.id ? { ...i, status: 'generating', progress: 0 } : i
      ));

      // Simulate 6 steps
      for (let step = 1; step <= 6; step++) {
        if (!runningRef.current) break;
        await new Promise(r => setTimeout(r, 900));
        onItemsChange(prev => prev.map(i =>
          i.id === item.id ? { ...i, progress: Math.round((step / 6) * 100) } : i
        ));
      }

      if (!runningRef.current) break;

      // Mark as done with a thumbnail
      const thumb = THUMBNAILS[Math.floor(Math.random() * THUMBNAILS.length)];
      onItemsChange(prev => prev.map(i =>
        i.id === item.id ? { ...i, status: 'done', progress: 100, thumbnail: thumb } : i
      ));

      // Small pause between items
      await new Promise(r => setTimeout(r, 400));
    }

    setIsRunning(false);
    runningRef.current = false;
  };

  const stopQueue = () => {
    runningRef.current = false;
    setIsRunning(false);
    // Reset any generating items back to pending
    onItemsChange(prev => prev.map(i =>
      i.status === 'generating' ? { ...i, status: 'pending', progress: 0 } : i
    ));
  };

  const base = darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />}
      <div className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm flex flex-col border-l transition-transform duration-300 ${base} ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-600 px-5 py-5 flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <i className="ri-stack-line text-white text-lg"></i>
              <h3 className="text-white font-bold text-lg">Generation Queue</h3>
              {items.length > 0 && (
                <span className="text-white/80 text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full">
                  {items.length} item{items.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 text-white transition-colors cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          <p className="text-white/75 text-xs">Queue up multiple prompts and generate them in batch</p>

          {/* Stats row */}
          {items.length > 0 && (
            <div className="flex items-center gap-3 mt-3">
              {pendingCount > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-white/50"></div>
                  <span className="text-white/80 text-xs">{pendingCount} pending</span>
                </div>
              )}
              {doneCount > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-300"></div>
                  <span className="text-white/80 text-xs">{doneCount} done</span>
                </div>
              )}
              {failedCount > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-300"></div>
                  <span className="text-white/80 text-xs">{failedCount} failed</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className={`px-4 py-3 border-b flex items-center gap-2 flex-shrink-0 ${border}`}>
          {isRunning ? (
            <button
              onClick={stopQueue}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold bg-red-500 text-white hover:bg-red-600 transition-all cursor-pointer"
            >
              <i className="ri-stop-line text-sm"></i>
              Stop Queue
            </button>
          ) : (
            <button
              onClick={runQueue}
              disabled={pendingCount === 0}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                pendingCount > 0
                  ? 'bg-gradient-to-r from-red-500 to-orange-600 text-white hover:shadow-md'
                  : darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-300 cursor-not-allowed'
              }`}
            >
              <i className="ri-play-line text-sm"></i>
              Run Queue
              {pendingCount > 0 && <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-[10px]">{pendingCount}</span>}
            </button>
          )}
          {doneCount > 0 && !isRunning && (
            <button
              onClick={clearDone}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title="Clear completed items"
            >
              <i className="ri-check-double-line text-sm"></i>
            </button>
          )}
          {failedCount > 0 && !isRunning && (
            <button
              onClick={retryFailed}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                darkMode ? 'bg-gray-800 text-amber-400 hover:bg-amber-900/30' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
              }`}
              title="Retry failed"
            >
              <i className="ri-refresh-line text-sm"></i>
            </button>
          )}
          {items.length > 0 && !isRunning && (
            <button
              onClick={clearAll}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                darkMode ? 'bg-gray-800 text-red-400 hover:bg-red-900/30' : 'bg-gray-100 text-red-500 hover:bg-red-50'
              }`}
              title="Clear all"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          )}
        </div>

        {/* How-to hint when empty */}
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 px-6 text-center">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <i className={`ri-stack-line text-2xl ${muted}`}></i>
            </div>
            <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Queue is empty</p>
            <p className={`text-xs ${muted} max-w-[220px] leading-relaxed`}>
              Use the <span className={`font-semibold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>&#34;Add to Queue&#34;</span> button below the Generate button to line up multiple videos, then run them all in batch.
            </p>
            <div className={`mt-6 rounded-xl p-4 text-left w-full ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${muted}`}>How it works</p>
              {['Write your prompt and settings', 'Click &#34;Add to Queue&#34;', 'Repeat for more videos', 'Click &#34;Run Queue&#34; to batch generate'].map((step, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                    darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                  }`}>{i + 1}</div>
                  <span className={`text-xs ${muted}`} dangerouslySetInnerHTML={{ __html: step }} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {items.map((item, idx) => (
              <div key={item.id} className={`rounded-xl border overflow-hidden transition-all ${cardBg} ${
                item.status === 'generating' ? (darkMode ? 'border-amber-700' : 'border-amber-400') :
                item.status === 'done' ? (darkMode ? 'border-green-800' : 'border-green-300') :
                item.status === 'failed' ? (darkMode ? 'border-red-800' : 'border-red-300') : ''
              }`}>
                {/* Thumbnail (done only) */}
                {item.thumbnail && item.status === 'done' && (
                  <div className="relative h-24 overflow-hidden">
                    <img src={item.thumbnail} alt={item.label} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-2 left-2">
                      <span className="flex items-center gap-1 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        <i className="ri-check-line text-[10px]"></i>Generated
                      </span>
                    </div>
                  </div>
                )}

                <div className="p-3">
                  <div className="flex items-start gap-2">
                    {/* Position number */}
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5 ${
                      item.status === 'done' ? 'bg-green-500 text-white' :
                      item.status === 'failed' ? 'bg-red-500 text-white' :
                      item.status === 'generating' ? 'bg-amber-500 text-white' :
                      darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                    }`}>
                      <i className={`${getStatusIcon(item.status)} text-[10px]`}></i>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold truncate mb-1 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        #{idx + 1} — {item.label}
                      </p>
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        <span className={getStatusBadge(item.status, darkMode)}>
                          {item.status === 'generating'
                            ? STEP_LABELS[Math.floor((item.progress / 100) * 5)] ?? 'Processing'
                            : item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                        {[item.style, item.duration, TYPE_LABELS[item.selectedType] ?? item.selectedType].map(tag => (
                          <span key={tag} className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                            darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                          }`}>{tag}</span>
                        ))}
                      </div>
                      <div className={`flex items-center justify-between text-[10px] ${muted}`}>
                        <span><i className="ri-time-line mr-0.5"></i>{formatTimeAgo(item.addedAt)}</span>
                        {item.status === 'generating' && (
                          <span className="text-amber-500 font-bold">{item.progress}%</span>
                        )}
                      </div>
                    </div>

                    {!isRunning && item.status !== 'generating' && (
                      <button
                        onClick={() => removeItem(item.id)}
                        className={`w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0 transition-all hover:bg-red-500 hover:text-white cursor-pointer ${muted}`}
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    )}
                  </div>

                  {/* Progress bar for generating items */}
                  {item.status === 'generating' && (
                    <div className={`mt-2 rounded-full h-1.5 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-700"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className={`px-4 py-3 border-t flex-shrink-0 ${border}`}>
          <p className={`text-[10px] text-center ${muted}`}>
            <i className="ri-information-line mr-1"></i>
            Queue is temporary — results shown until you clear or refresh
          </p>
        </div>
      </div>
    </>
  );
}
